'use client';

import { useEffect, useState, useMemo } from 'react';
import Link from 'next/link';
import { Alternative, AlternativeCategory, CATEGORY_LABELS, CATEGORY_ICONS, CATEGORY_DESCRIPTIONS } from '@dependra/shared';
import { Search, X, SlidersHorizontal, ArrowUpDown, ExternalLink, CheckCircle, Leaf, MapPin } from 'lucide-react';
import { API_BASE } from '@/config/api.config';

type SortOption = 'name' | 'country' | 'newest';

export default function AlternativesPage() {
  const [alternatives, setAlternatives] = useState<Alternative[]>([]);
  const [categories, setCategories] = useState<{ category: AlternativeCategory; count: number }[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<AlternativeCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [showFilters, setShowFilters] = useState(false);
  const [filterGdpr, setFilterGdpr] = useState(false);
  const [filterGreen, setFilterGreen] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedCategory]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const altsUrl = selectedCategory 
        ? `${API_BASE}/alternatives?category=${selectedCategory}&limit=100`
        : `${API_BASE}/alternatives?limit=100`;
      
      const [altsRes, catsRes] = await Promise.all([
        fetch(altsUrl),
        fetch(`${API_BASE}/alternatives/categories?fresh=true`),
      ]);

      const altsData = await altsRes.json();
      const cats: { category: AlternativeCategory; count: number }[] = await catsRes.json();

      const alts = altsData.data || altsData;
      setAlternatives(Array.isArray(alts) ? alts : []);
      setCategories(Array.isArray(cats) ? cats : []);
    } catch (error) {
      console.error('Failed to fetch alternatives:', error);
      setAlternatives([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Get all unique "replaces" values for search suggestions
  const allReplaces = useMemo(() => {
    const replaces = new Set<string>();
    alternatives.forEach(alt => {
      alt.replaces?.forEach(r => replaces.add(r));
    });
    return Array.from(replaces).sort((a, b) => a.localeCompare(b));
  }, [alternatives]);

  // Filter and sort alternatives
  const filteredAlternatives = useMemo(() => {
    let result = [...alternatives];

    // Search filter (name, tagline, description, replaces)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(alt => 
        alt.name.toLowerCase().includes(query) ||
        alt.tagline?.toLowerCase().includes(query) ||
        alt.description?.toLowerCase().includes(query) ||
        alt.replaces?.some(r => r.toLowerCase().includes(query))
      );
    }

    // GDPR filter
    if (filterGdpr) {
      result = result.filter(alt => alt.gdprNative);
    }

    // Green energy filter
    if (filterGreen) {
      result = result.filter(alt => alt.greenEnergy);
    }

    // Sort
    switch (sortBy) {
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'country':
        result.sort((a, b) => (a.country || '').localeCompare(b.country || ''));
        break;
      case 'newest':
        result.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
        break;
    }

    return result;
  }, [alternatives, searchQuery, sortBy, filterGdpr, filterGreen]);

  // Group by category
  const groupedAlternatives: Record<AlternativeCategory, Alternative[]> = useMemo(() => {
    return filteredAlternatives.reduce((acc, alt) => {
      if (!acc[alt.category]) {
        acc[alt.category] = [];
      }
      acc[alt.category].push(alt);
      return acc;
    }, {} as Record<AlternativeCategory, Alternative[]>);
  }, [filteredAlternatives]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory(null);
    setFilterGdpr(false);
    setFilterGreen(false);
    setSortBy('name');
  };

  const hasActiveFilters = searchQuery || selectedCategory || filterGdpr || filterGreen;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 py-16 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/30 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-500/30 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-5xl mx-auto px-4 text-center relative z-10">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
            <span className="text-2xl">🇪🇺</span>
            <span className="text-white/90 text-sm font-medium">European Alternatives Directory</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Find EU-Based Tech{" "}
            <span className="block text-blue-200">Alternatives</span>
          </h1>
          
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Replace US tech giants with GDPR-compliant European solutions. 
            Support digital sovereignty and reduce compliance risks.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search alternatives or type a US product to replace (e.g., Dropbox, AWS, Slack)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-12 py-4 rounded-2xl bg-white shadow-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-4 focus:ring-blue-300/50 text-lg"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              )}
            </div>
            
            {/* Quick search suggestions */}
            {!searchQuery && allReplaces.length > 0 && (
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                <span className="text-blue-200 text-sm">Popular:</span>
                {['AWS', 'Google Analytics', 'Dropbox', 'Slack', 'Stripe'].map(term => (
                  <button
                    key={term}
                    onClick={() => setSearchQuery(term)}
                    className="px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm transition border border-white/20"
                  >
                    {term}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 max-w-md mx-auto">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white">{categories.reduce((sum, c) => sum + c.count, 0)}</div>
              <div className="text-sm text-blue-200">Alternatives</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white">{categories.length}</div>
              <div className="text-sm text-blue-200">Categories</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white">100%</div>
              <div className="text-sm text-blue-200">EU-Hosted</div>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Filter Bar */}
      <section className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Category Pills */}
            <div className="flex-1 overflow-x-auto pb-2 lg:pb-0">
              <div className="flex gap-2 min-w-max">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition whitespace-nowrap ${selectedCategory === null
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All ({categories.reduce((sum, c) => sum + c.count, 0)})
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.category}
                    onClick={() => setSelectedCategory(cat.category)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition whitespace-nowrap ${
                      selectedCategory === cat.category
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {CATEGORY_ICONS[cat.category]} {CATEGORY_LABELS[cat.category]} ({cat.count})
                  </button>
                ))}
              </div>
            </div>

            {/* Filter & Sort Controls */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                  showFilters || filterGdpr || filterGreen
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <SlidersHorizontal className="w-4 h-4" />
                Filters
                {(filterGdpr || filterGreen) && (
                  <span className="w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                    {(filterGdpr ? 1 : 0) + (filterGreen ? 1 : 0)}
                  </span>
                )}
              </button>

              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="appearance-none pl-4 pr-10 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="name">Sort: A-Z</option>
                  <option value="country">Sort: Country</option>
                  <option value="newest">Sort: Newest</option>
                </select>
                <ArrowUpDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-3">
              <button
                onClick={() => setFilterGdpr(!filterGdpr)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                  filterGdpr
                    ? 'bg-green-100 text-green-800 border border-green-300'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <CheckCircle className="w-4 h-4" />
                GDPR Native
              </button>
              <button
                onClick={() => setFilterGreen(!filterGreen)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                  filterGreen
                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Leaf className="w-4 h-4" />
                Green Energy
              </button>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition"
                >
                  <X className="w-4 h-4" />
                  Clear all
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Alternatives Grid */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        {/* Results count */}
        {!loading && (
          <div className="mb-8 flex items-center justify-between">
            <p className="text-gray-600">
              Showing <span className="font-semibold text-gray-900">{filteredAlternatives.length}</span> alternatives
              {searchQuery && (
                <span> for "<span className="font-medium">{searchQuery}</span>"</span>
              )}
            </p>
          </div>
        )}

        {loading && (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading alternatives...</p>
          </div>
        )}

        {!loading && filteredAlternatives.length === 0 && (
          <div className="text-center py-20 max-w-md mx-auto">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Search className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No alternatives found</h3>
            <p className="text-gray-600 mb-6">
              {searchQuery 
                ? `We couldn't find any alternatives matching "${searchQuery}". Try a different search term or browse all categories.`
                : 'No alternatives match your current filters. Try adjusting your filters.'}
            </p>
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
            >
              <X className="w-4 h-4" />
              Clear all filters
            </button>
          </div>
        )}

        {!loading && filteredAlternatives.length > 0 && (
          <div className="space-y-16">
            {Object.entries(groupedAlternatives).map(([category, alts]) => (
              <div key={category} id={category.toLowerCase()}>
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-3xl">{CATEGORY_ICONS[category as AlternativeCategory]}</span>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                      {CATEGORY_LABELS[category as AlternativeCategory]}
                    </h2>
                    <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                      {alts.length}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm ml-12">
                    {CATEGORY_DESCRIPTIONS[category as AlternativeCategory]}
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {alts.map((alt) => (
                    <div
                      key={alt.id}
                      className="group relative bg-white rounded-2xl border border-gray-200 hover:border-transparent hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-500 overflow-hidden flex flex-col"
                    >
                      {/* Top accent gradient bar */}
                      <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      
                      <div className="p-6 flex flex-col flex-grow">
                        {/* Header */}
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                                {alt.name}
                              </h3>
                              {alt.greenEnergy && (
                                <span className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center" title="Powered by green energy">
                                  <Leaf className="w-3 h-3 text-emerald-600" />
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 flex items-center gap-1.5">
                              <span className="text-base">🇪🇺</span>
                              {alt.country}
                            </p>
                          </div>
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl flex items-center justify-center text-2xl shadow-sm group-hover:scale-110 group-hover:shadow-md transition-all duration-300">
                            {CATEGORY_ICONS[alt.category]}
                          </div>
                        </div>

                        {/* Badges */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {alt.gdprNative && (
                            <span className="inline-flex items-center gap-1.5 text-xs bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 px-3 py-1.5 rounded-full font-semibold border border-green-200 shadow-sm">
                              <CheckCircle className="w-3.5 h-3.5" />
                              GDPR Native
                            </span>
                          )}
                          {alt.localFirst && (
                            <span className="inline-flex items-center gap-1.5 text-xs bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 px-3 py-1.5 rounded-full font-semibold border border-blue-200 shadow-sm">
                              <MapPin className="w-3.5 h-3.5" />
                              Local First
                            </span>
                          )}
                        </div>

                        {/* Tagline */}
                        <p className="text-gray-800 font-semibold text-sm mb-2 leading-snug">{alt.tagline}</p>
                        
                        {/* Description */}
                        <p className="text-sm text-gray-500 mb-4 line-clamp-2 flex-grow leading-relaxed">{alt.description}</p>

                        {/* Features */}
                        {alt.features && alt.features.length > 0 && (
                          <div className="mb-4 bg-gray-50 rounded-xl p-3">
                            <ul className="space-y-2">
                              {alt.features.slice(0, 3).map((feature) => (
                                <li key={feature} className="text-xs text-gray-700 flex items-start gap-2">
                                  <div className="w-4 h-4 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <CheckCircle className="w-2.5 h-2.5 text-blue-600" />
                                  </div>
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Replaces Section */}
                        {alt.replaces && alt.replaces.length > 0 && (
                          <div className="mb-4">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Replaces</p>
                            <div className="flex flex-wrap gap-1.5">
                              {alt.replaces.slice(0, 4).map((r) => (
                                <span
                                  key={r}
                                  className="text-xs bg-gradient-to-r from-red-50 to-orange-50 text-red-700 px-2.5 py-1 rounded-lg font-medium border border-red-100"
                                >
                                  {r}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Pricing */}
                        {alt.pricing && (
                          <div className="mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl px-4 py-3 border border-blue-100">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-gray-500">Starting at</span>
                              <span className="text-sm font-bold text-blue-700">{alt.pricing}</span>
                            </div>
                          </div>
                        )}

                        {/* CTA Button */}
                        {alt.website && (
                          <a
                            href={alt.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-auto inline-flex items-center justify-center gap-2 w-full bg-gradient-to-r from-gray-900 to-gray-800 hover:from-blue-600 hover:to-indigo-600 text-white font-semibold py-3.5 px-4 rounded-xl transition-all duration-300 text-sm shadow-lg shadow-gray-900/10 hover:shadow-blue-500/25 hover:scale-[1.02]"
                          >
                            Visit Website
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* List Your Company CTA */}
      <section className="max-w-6xl mx-auto px-4 pb-12">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-8 md:p-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">List Your EU Company</h3>
              <p className="text-gray-600 max-w-xl">
                Are you building a privacy-focused, GDPR-compliant European solution? 
                Join our directory and help organizations find EU alternatives.
              </p>
            </div>
            <Link
              href="/submit-company"
              className="flex-shrink-0 inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-700 transition shadow-lg shadow-blue-500/25"
            >
              Submit Your Company
              <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-blue-500/20 border border-blue-400/30">
            <span className="text-blue-400 text-sm font-medium">🔍 Free Assessment</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Assess Your Tech Stack
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Upload your vendor list and get personalized EU alternative recommendations based on your compliance requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/register"
              className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold py-4 px-8 rounded-xl hover:bg-blue-700 transition shadow-lg shadow-blue-500/25"
            >
              Start Free Scan
              <ExternalLink className="w-4 h-4" />
            </Link>
            <Link
              href="/demo"
              className="inline-flex items-center justify-center gap-2 bg-white/10 text-white font-semibold py-4 px-8 rounded-xl hover:bg-white/20 transition border border-white/20"
            >
              View Demo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
